import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import type { AppState } from '@nextgen-web-framework/core';
import { Logger } from '@nextgen-web-framework/core';
import {
  useTranslation,
  Input,
  InputTypes,
  MLPVersion,
  InputValidations,
  Theme,
  Drawer,
  AlertMessage,
  AccountType,
  SkeletonFrame,
} from '@nextgen-web-framework/all';
import {
  SponsorSearchProps,
  profileApi,
  SponsorSearchResponseInner,
  WithoutPostalCodeLeadsSearchProps,
} from '@commerce-profile-webcomponents/sdk';
import logObject from '../../../../logObject';
import { SponsorCard } from '../SponsorCard/SponsorCard';
import { DoNotKnowSponsorFlow } from '../types/propTypes';
import styles from './LeadsSearch.module.scss';
import { LeadsSearchProps } from './types/propTypes';

const LeadsSearch = ({ isFieldDisabled, callback, doNotKnowAboFlowType, state, city }: LeadsSearchProps) => {
  const logger = Logger.getInstance(logObject('LeadsSearch.tsx', 'LeadsSearch'));
  const [sponsorResult, setSponsorResult] = useState<SponsorSearchResponseInner[]>([]);
  const selectedAccountType: AccountType = useSelector((state: AppState) => state.commerceProfile.selectedAccountType);
  //const { selectSponsor } = useSelector((state: AppState) => state.commerceProfile.registrationFlowData);
  const t = useTranslation();
  const { BusinessOwner } = AccountType;
  const [postCodeTxt, setPostCodeTxt] = useState<string>('');
  const [verificationStatus, setVerificationStatus] = useState<InputValidations>(InputValidations.DEFAULT);
  const [showLoadingComp, setShowLoadingComp] = useState(false);
  const [showAlertMessage, setShowAlertMessage] = useState(false);
  const skeltonArr = [1, 2, 3];

  logger.debug('BusinessOwner', BusinessOwner);
  const handlePostalCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value.length === 0) {
      setSponsorResult([]);
    }
    setPostCodeTxt(e.target.value);
    setVerificationStatus(InputValidations.DEFAULT);
  };
  // useEffect(() => {
  //   setSponsorLocal(sponsor);
  // }, [sponsor]);

  const onEnterSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      onLeadsSearchClick({
        sponsorInputValue: postCodeTxt,
        accountType: selectedAccountType,
      });
    }
  };
  const onLeadsSearchClick = (payload: SponsorSearchProps) => {
    setShowLoadingComp(true);
    setShowAlertMessage(true);
    profileApi
      .getLeadsListByPostalCode({
        ...payload,
      })
      .then((res: SponsorSearchResponseInner[]) => {
        setShowLoadingComp(false);
        setSponsorResult(res);
        setVerificationStatus(InputValidations.DEFAULT);
      })
      .catch(() => {
        setShowLoadingComp(false);
        setVerificationStatus(InputValidations.INVALID);
        setSponsorResult([]);
      });
  };

  const leadsSearchWithoutPostalCode = (payload: WithoutPostalCodeLeadsSearchProps) => {
    setShowLoadingComp(true);
    setShowAlertMessage(true);

    profileApi
      .getLeadsListWithoutPostalCode({
        ...payload,
      })
      .then((res: SponsorSearchResponseInner[]) => {
        setShowLoadingComp(false);
        setSponsorResult(res);
        setVerificationStatus(InputValidations.DEFAULT);
      })
      .catch(() => {
        setShowLoadingComp(false);
        setVerificationStatus(InputValidations.INVALID);
        setSponsorResult([]);
      });
  };

  const handleSelectwithpostCode = (abo: SponsorSearchResponseInner | null) => {
    callback(abo);
  };
  const drawerCloseHandler = () => {
    setPostCodeTxt(''); //do we need it since we are removing the component?
    setSponsorResult([]);
    callback(null);
  };

  const postalCodeInputField = () => (
    <div className={styles['sponsor-field']}>
      <>
        <Input
          id="postalCode"
          data-testid="postalCode"
          type={InputTypes.NUMBER}
          name="postalCode"
          value={postCodeTxt}
          label={t('profile.SelectSponsor.enterZipCode')}
          onChange={handlePostalCodeChange}
          maxLength={50}
          mlpVersion={MLPVersion.TWO}
          className={`${postCodeTxt ? styles['verify-link'] : ''}`}
          status={verificationStatus}
          onKeyDown={onEnterSearch}
          disabled={isFieldDisabled}
          message={
            verificationStatus === InputValidations.INVALID
              ? t('profile.SinglePageRegistrationStep.ZipCodeinputError')
              : ''
          }
          theme={verificationStatus === InputValidations.INVALID ? Theme.ERROR : Theme.BLACK}
          ctaTextLink={{
            text: t('profile.SinglePageRegistrationStep.search'),
            id: 'btn_verify',
            onClick: () => {
              onLeadsSearchClick({ sponsorInputValue: postCodeTxt, accountType: selectedAccountType });
            },
            disabled: postCodeTxt ? false : true,
          }}
        />
      </>
    </div>
  );
  useEffect(() => {
    if (doNotKnowAboFlowType == DoNotKnowSponsorFlow.WITHOUT_POSTAL_CODE_SEARCH) {
      leadsSearchWithoutPostalCode({ accountType: selectedAccountType, state: state, city: city });
    }
  }, [doNotKnowAboFlowType]);
  const DontKnowABODrawer = () => (
    <>
      {doNotKnowAboFlowType == DoNotKnowSponsorFlow.POSTAL_CODE_SEARCH && postalCodeInputField()}
      {postCodeTxt && showAlertMessage && (
        <AlertMessage className={styles['registration-select-sponsor-result-msg']} theme={Theme.ACTION}>
          {t('profile.RegistrationSponsor.suggestABONote')}
        </AlertMessage>
      )}

      {showLoadingComp
        ? skeltonArr.map((index) => (
            <div className={styles['skeleton-loader-wrapper']} key={index}>
              <SkeletonFrame heightValue="54" isRoundCorner={true} borderRadiusValue="8" />
            </div>
          ))
        : ''}
      {sponsorResult.length > 0 &&
        sponsorResult.map((abo: SponsorSearchResponseInner) => (
          <SponsorCard key={abo.accountNumber} abo={abo} addHandler={handleSelectwithpostCode} />
        ))}
    </>
  );
  const containerStyle = styles['sponsor-container'];

  return (
    <div className={containerStyle} data-testid="sponsor-container">
      <Drawer
        header={t('profile.SelectSponsor.dontknowABO')}
        data-testid="abo-drawer-test-id"
        closeIcon
        className="sponsor-drawer"
        onClose={drawerCloseHandler}
        bottomGap={0}
      >
        <div className={styles['sponsor-drawer']}>{DontKnowABODrawer()}</div>
      </Drawer>
    </div>
  );
};

export default LeadsSearch;
